import { EventResponseModel } from '@3ddv/igsu-models';
import { TicketsPerEvent } from '@3ddv/igsu-models/definitions/event.model';
import { EventModel, ReturnedTicketsData } from '@3ddv/utils-igsu';
import { Injectable, Injector, NgZone, assertInInjectionContext, inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { SelectedTicketModel, TicketModel } from 'projects/common/igsu-models/definitions/ticket.model';
import { BehaviorSubject, map, take } from 'rxjs';
import { SectionsService } from '../../_shared/data-access/sections.service';
import { EventsService } from './events.service';
import { PriceScalesService } from './price-scales.service';

/**
 * EventsTicketsService is an injectable service that manages ticket data for events.
 * It provides functionalities to select, unselect, and manage the tickets using
 * RxJS's BehaviorSubjects. It interacts with the EventsService to manage and
 * validate ticket selections based on the current event's context.
 */
@Injectable({
  providedIn: 'root',
})
export class EventsTicketsService {
  private _ngZone = inject(NgZone);
  private _injector = inject(Injector);
  private _sectionsService = inject(SectionsService);

  private readonly _ticketData = new BehaviorSubject<EventResponseModel['tickets'] | null>(null);
  public readonly ticketData$ = this._ticketData.asObservable();

  private readonly _selectedTickets = new BehaviorSubject<SelectedTicketModel>([]);
  public readonly selectedTickets$ = this._selectedTickets.asObservable();

  /**
   * A collection of ticket counts per event.
   */
  public ticketsPerEvent!: TicketsPerEvent[];

  /**
   * Retrieves the current ticket data.
   * @returns The current value of ticket data.
   */
  public getTicketData = () => {
    return this._ticketData.getValue();
  };

  /**
   * Sets the ticket data.
   * @param tickets The tickets data to set.
   */
  public setTicketData = (tickets: EventResponseModel['tickets']) => {
    this._ticketData.next(tickets);
  };

  /**
   * Retrieves the selected tickets.
   * @returns The current value of selected tickets.
   */
  public getSelectedTicketsData = () => {
    return this._selectedTickets.getValue();
  };

  /**
   * Toggles the selection state of a ticket. If it is already selected, it will be unselected and vice versa.
   * @param event The DOM event, used to stop propagation.
   * @param ticket The ticket model to toggle.
   */
  public toggleTicketSelection = (event: Event, ticket: TicketModel) => {
    const ticketID = ticket.id;

    this._ngZone.run(() => {
      this.selectedTickets$.pipe(take(1)).subscribe(currentSelectedTickets => {
        let newSelectedTickets: SelectedTicketModel;

        const isTicketSelected = currentSelectedTickets?.some(selectedTicket => selectedTicket.id === ticketID);

        if (currentSelectedTickets && isTicketSelected) {
          newSelectedTickets = currentSelectedTickets.filter(ticket => ticket.id !== ticketID);
        } else {
          newSelectedTickets = currentSelectedTickets ? [...currentSelectedTickets, ticket] : [ticket];
        }

        this._selectedTickets.next(newSelectedTickets);
      });
    });
  };

  /**
   * Selects all tickets associated with a particular order ID.
   * @param orderID The order ID to select all tickets for.
   */
  public selectAllOrderTickets = (orderID: TicketModel['orderID']) => {
    this._ngZone.run(() => {
      this.unselectAllTickets();

      const eventsService = this._injector.get(EventsService);
      const eventId = eventsService.selectedEvent!.id.toString();

      this.ticketData$
        .pipe(
          take(1),
          map(ticketsData => ticketsData?.[eventId]?.[orderID])
        )
        .subscribe(orderTickets => {
          if (!orderTickets) return;

          this._selectedTickets.next(orderTickets);
        });
    });
  };

  /**
   * Checks if any ticket is selected.
   * @returns A boolean indicating if any ticket is selected.
   */
  public isSomeTicketSelected = () => {
    let isSelected = false;
    this.selectedTickets$.pipe(take(1)).subscribe(ticketData => {
      if (ticketData?.length) {
        isSelected = true;
      }
    });

    return isSelected;
  };

  /**
   * Checks if a specific ticket is selected.
   * @param ticketID The ID of the ticket to check.
   * @param selectedTickets The current array of selected tickets.
   * @returns A boolean indicating if the specified ticket is selected.
   */
  public isTicketSelected = (ticketID: TicketModel['id'], selectedTickets: SelectedTicketModel) => {
    if (!selectedTickets?.length) return false;
    return selectedTickets.some(selectedTicket => selectedTicket.id === ticketID);
  };

  /**
   * Unselects all currently selected tickets.
   */
  public unselectAllTickets = () => {
    this._selectedTickets.next([]);
  };

  /**
   * Retrieves the number of tickets per event based on the given event ID.
   * @param id The ID of the event to retrieve ticket count for.
   * @returns The number of tickets for the specified event.
   */
  public getTicketCountPerEvent = (id: EventModel['id']) => {
    const event = this.ticketsPerEvent.find(ev => ev.eventId === id);
    if (!event) return 0;

    return event?.numberOfTickets;
  };

  /**
   * Retrieves the IDs of all selected tickets.
   * @returns An array of selected ticket IDs.
   */
  public getSelectedTicketIds = () => {
    if (!this._selectedTickets.getValue()) return;

    return this._selectedTickets.getValue()?.map(ticket => ticket.id.toString());
  };

  /**
   * Retrieves the IDs of all selected tickets.
   * @returns An array of selected ticket IDs.
   */
  public getReturnTicketsData = () => {
    const selectedTickets = this._selectedTickets.getValue();
    const selectedSection = this._sectionsService.getSelectedSectionData();
    const selectedPriceScale = this._injector.get(PriceScalesService).getSelectedPriceScale();

    if (!selectedTickets || !selectedSection) return;

    return selectedTickets.map(ticket => {
      const selectedPriceScaleData = selectedSection?.priceScales.find(
        priceScale => priceScale.priceScaleID === selectedPriceScale
      );

      const originalBuyerType = selectedPriceScaleData?.buyerTypes.find(buyerTypeIndex => {
        return +buyerTypeIndex.buyerType.buyerTypeID === +ticket.buyerType && buyerTypeIndex.availableQuantity;
      });

      return {
        id: ticket.id.toString(),
        // @ts-ignore
        buyerType: originalBuyerType ?? ticket.buyerType,
        price: ticket.price.value,
        section: ticket.section,
        shouldKeepBuyerType: !!originalBuyerType,
      } satisfies ReturnedTicketsData;
    });
  };
}

export const eventConfigurationResolver: ResolveFn<any> = () => {
  assertInInjectionContext((err: any) => console.log('Injector context error: ', err));
  return inject(EventsService).selectEventConfiguration();
};
